import type {
  Referral as PrismaReferral,
  ReferralStatus as PrismaReferralStatus,
  PreferredContactMethod,
  ContactPreference,
  XRay,
  Document,
  ReferralReasonGroup as PrismaReferralReasonGroup,
} from '@prisma/client';

import type { Doctor } from './doctor';

// Re-export the Prisma-generated types
export type ReferralStatus = PrismaReferralStatus;
export type { PreferredContactMethod, ContactPreference };

export type ReferralSubmissionMode = 'SEND_NOW' | 'SIGN_AND_SEND';

// Define ReferralType as a const object with type assertion instead of an enum
export const ReferralType = {
  URGENT: 'URGENT',
  ROUTINE: 'ROUTINE',
  SYMPTOMATIC: 'SYMPTOMATIC',
  ELECTIVE: 'ELECTIVE',
  CONSULTATION: 'CONSULTATION',
  TREATMENT: 'TREATMENT',
  CONSULTATION_AND_TREATMENT: 'CONSULTATION_AND_TREATMENT',
} as const;

// Create a type from the values of the const object
export type ReferralType = (typeof ReferralType)[keyof typeof ReferralType];

// Define the structure for referral reason groups
export interface ReferralReasonGroup extends PrismaReferralReasonGroup {}

// Define the structure for new referral reason groups
export interface NewReferralReasonGroup {
  reasonCode: string;
  reasonLabel?: string | null;
  selectedTeeth: number[];
  selectedPediatricTeeth: string[];
  diagnosis: string[];
  notes?: string | null;
  booleanAnswers?: Record<string, boolean> | null;
  dateValues?: Record<string, string | null>;
  selectedProceduresByGroup?: Record<string, string | string[]> | null;
  /** Form-only: re-entered tooth numbers for verification when embed requireToothVerification is on; not persisted */
  toothVerificationText?: string;
}

// Define the structure for new XRay entries
export interface NewXRay {
  filename: string; // Display Name
  originalFilename?: string; // S3 Key
  dateOfXRay: Date | null;
  isDateUnknown: boolean;
}

// Define the structure for new Document entries
export interface NewDocument {
  filename: string; // Display Name
  originalFilename?: string; // S3 Key
  dateOfDoc: Date | null;
}

// Extend/modify the Prisma-generated Referral type if needed
export interface Referral extends Omit<PrismaReferral, 'preferredContactMethods'> {
  xrays: XRay[];
  documents: Document[];
  reasonGroups: ReferralReasonGroup[];
  preferredContactMethods?: PreferredContactMethod[];
  receivingDoctor: Doctor;
  referringDoctor: Doctor;
  /** Pre-forward referring doctor, set only when a REFERRED_OUT forward kept both. */
  secondaryReferringDoctor?: Doctor | null;
  /** Who this referral was originally sent to, before any forwarding. Set once, on the
   *  first forward only — stays correct across any number of subsequent forwards. */
  originalReceivingDoctor?: Doctor | null;
  originalReceivingOffice?: { name: string } | null;
  /** Who this referral originally came from, before any forwarding. Set once, on the
   *  first forward only — stays correct across any number of subsequent forwards. */
  originalReferringDoctor?: Doctor | null;
  originalReferringOffice?: { name: string } | null;
  signer?: {
    id: string;
    firstName: string;
    lastName: string;
    role?: string;
    degree?: string | null;
  } | null;
  signedOnBehalfOf?: {
    id: string;
    firstName: string;
    lastName: string;
    role?: string;
    degree?: string | null;
  } | null;
  /** Office that received this referral (when set, e.g. from embed form); use for display over receivingDoctor.office */
  receivingOffice?: { name: string } | null;
}

export type ReferralAttachmentWithUrls = {
  url?: string;
  downloadUrl?: string;
};

/** Referral shape returned by the referral detail loader (files include signed URLs). */
export type ReferralDetailReferral = Referral & {
  xrays: Array<XRay & ReferralAttachmentWithUrls>;
  documents: Array<Document & ReferralAttachmentWithUrls>;
};

// Keep our NewReferral type
export interface NewReferral {
  patientFirstName: string;
  patientLastName: string;
  patientEmail: string | null;
  patientPhone: string | null;
  patientDob: Date;
  patientPronouns: string | null;
  patientGender: string | null;
  patientDentalInsuranceCompany: string | null;
  customPronouns: string | null;
  customGender: string | null;
  guardianName: string | null;
  referringDoctor: string;
  receivingDoctor: string;
  specialty: string;
  referralType: ReferralType;
  chiefComplaint: string;
  notes: string | null;
  reasonGroups: NewReferralReasonGroup[]; // New relation to reason groups
  xrays: NewXRay[];
  documents: NewDocument[];
  preferredContactMethods: PreferredContactMethod[];
  contactPreference: ContactPreference;

  // Fields for embedded forms
  sourceUrl?: string;
  isEmbedded?: boolean;
  embeddedFormKeyUsed?: string;
  embedSubmissionId?: string | null;

  // Referring provider information for embedded forms
  referringProviderName?: string | null;
  referringOfficeName?: string | null;
  referringOfficeEmail?: string | null;
  referringOfficePhone?: string | null;

  /** Clinical/business referral date (Self Referred); omit for normal/embed creates. */
  dateOfReferral?: Date | null;

  // Office tracking for reporting (which office referred/received)
  referringOfficeId?: string | null;
  receivingOfficeId?: string | null;

  submissionMode: ReferralSubmissionMode;
  /** Sign & Send: defer the patient insurance invite until the draft is signed. */
  patientInsuranceInvitePending?: boolean;
}

// Serialized version for Remix loaders
export type SerializedReferral = Omit<
  Referral,
  | 'patientDob'
  | 'createdAt'
  | 'updatedAt'
  | 'dateOfReferral'
  | 'forwardedAt'
  | 'xrays'
  | 'documents'
  | 'reasonGroups'
> & {
  patientDob: string;
  createdAt: string;
  updatedAt: string;
  dateOfReferral: string | null;
  forwardedAt: string | null;
  /** Effective "sent" timestamp for the outgoing dashboard: forwardedAt when set, else createdAt. */
  sentAt?: string;
  latestNotificationAt: string | null;
  unreadMentionCount: number;
  isArchived: boolean | undefined;
  reasonGroups: Array<
    Omit<Referral['reasonGroups'][number], 'createdAt' | 'updatedAt'> & {
      createdAt: string;
      updatedAt: string;
    }
  >;
  xrays: Array<
    Omit<Referral['xrays'][number], 'createdAt' | 'updatedAt' | 'dateOfXRay'> & {
      createdAt: string;
      updatedAt: string;
    }
  >;
  documents: Array<
    Omit<Referral['documents'][number], 'createdAt' | 'updatedAt' | 'dateOfDoc'> & {
      createdAt: string;
      updatedAt: string;
      dateOfDoc: string | null;
    }
  >;
  /** When set, this referral was created from a multi-office embed submission */
  embedSubmissionId?: string | null;
  /** Number of referrals in the same embed submission (including this one); only when embedSubmissionId is set */
  embedSubmissionSiblingCount?: number;
  /** Office names for all referrals in the same embed submission; only when embedSubmissionId is set */
  embedSubmissionOfficeNames?: string[];
};
